import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { declineManualOrder, mapManualApprovalError, useTranslation } from '@dukanti/shared';
import type { DeclineManualOrderResult } from '@dukanti/shared';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

export interface UseDeclineManualOrderOptions {
  readonly orderId: string;
  readonly storeId: string | null;
}

export function useDeclineManualOrder(
  opts: UseDeclineManualOrderOptions,
): UseMutationResult<DeclineManualOrderResult, unknown, void> {
  const { orderId, storeId } = opts;
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation<DeclineManualOrderResult, unknown, void>({
    mutationFn: () => declineManualOrder(supabase, orderId),
    onSuccess: (result) => {
      if (!result.success) {
        toast.error(t(mapManualApprovalError(undefined, result, 'decline')));
        return;
      }
      if (result.order) {
        queryClient.setQueryData(
          ['admin-order-detail', orderId],
          (old: { order: unknown; items: unknown; events: unknown } | undefined) =>
            old ? { ...old, order: result.order } : old,
        );
      }
      if (storeId) {
        void queryClient.invalidateQueries({ queryKey: ['store-orders', storeId] });
      }
      void queryClient.invalidateQueries({ queryKey: ['incoming-orders-count-user'] });
      void queryClient.invalidateQueries({ queryKey: ['stock-summary'] });
      void queryClient.invalidateQueries({ queryKey: ['notification-diagnostics'] });
      toast.success(t('admin.orders.manualApproval.declineSuccess' as never));
    },
    onError: (err) => {
      toast.error(t(mapManualApprovalError(err, undefined, 'decline')));
    },
  });
}
